import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to track if a component is currently mounted
 * 
 * This hook helps prevent React state updates on unmounted components,
 * which can cause memory leaks and the "Can't perform a React state update
 * on an unmounted component" warning.
 * 
 * @returns A function that returns true if the component is mounted, false otherwise
 * 
 * @example
 * ```tsx
 * const isMounted = useIsMounted();
 * 
 * useEffect(() => {
 *   fetchData().then(data => {
 *     if (isMounted()) {
 *       setData(data);
 *     }
 *   });
 * }, []);
 * ```
 */
export function useIsMounted(): () => boolean {
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  return useCallback(() => isMountedRef.current, []);
}
